import { useEffect } from 'react';
import { InputCaptureManager, InputAccessRights } from './InputCaptureManager';
import { handleEvent } from '../../api/modules/hostMessaging/inputEvents';

interface UseInputCaptureManagerOptions {
  access: InputAccessRights;
  active: boolean;
}

/**
 * React hook that manages an InputCaptureManager instance.
 *
 * Replicates the /q/ (marketing-demo) input pipeline for /j/ links:
 * - Creates InputCaptureManager with direct callbacks to handleEvent
 * - Bypasses captureStore entirely for events (no React re-render in the event path)
 * - Forces touch: true so touch listeners are always active browser-side
 *   (host-side Playjector filters when touchEnabled is false)
 * - Filters gamepad overlay touches via isGamepadOverlayElement (prevents dual processing)
 */
export function useInputCaptureManager({ access, active }: UseInputCaptureManagerOptions): void {
  const { mouse, keyboard, gamepad } = access;

  useEffect(() => {
    if (!active) return;

    const manager = new InputCaptureManager({
      access: {
        mouse,
        keyboard,
        gamepad,
        touch: true,
      },
      onKeyboard: (event) => handleEvent(event),
      onMouse: (event) => handleEvent(event),
      onTouch: (event) => handleEvent(event),
      onGamepad: (gamepads) => {
        const event = new CustomEvent('gamepad', { detail: gamepads });
        handleEvent(event);
      },
    });

    manager.start();

    return () => {
      manager.cleanup();
    };
  }, [active, mouse, keyboard, gamepad]);
}
